import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export interface PrescriptionFilter {
    nre?: string;
    patientName?: string;
    patientFiscalCode?: string;
    dateFrom?: string;
    dateTo?: string;
    syncStatus?: 'pending' | 'synced' | 'failed';
    healthnetSynced?: boolean;
    page?: number;
    limit?: number;
}

export interface PrescriptionPreview {
    id: string;
    nre: string;
    prescriptionDate: string;
    patient: {
        fiscalCode: string;
        name: string;
        firstName: string;
        lastName: string;
    };
    doctor: {
        name: string;
        firstName: string;
        lastName: string;
    };
    diagnosis: string;
    syncStatus: 'pending' | 'synced' | 'failed';
    healthnetSynced: boolean;
    localStatus: 'new' | 'existing';
    serviceCount: number;
    detailsJson: any[];
    rawResponse: any;
    selected: boolean;
    enriched?: boolean; // Flag to indicate if patient data was enriched
}

export interface Prescription {
    id: string;
    nre: string;
    recup_id?: string;
    patient_id?: string;
    patient_fiscal_code?: string;
    patient_first_name?: string;
    patient_last_name?: string;
    patient_full_name?: string; // Complete patient name from enrichment
    patient_birth_date?: string;
    doctor_id?: string;
    doctor_first_name?: string;
    doctor_last_name?: string;
    doctor_code?: string;
    prescription_date?: string;
    date_of_completion?: string;
    diagnosis_name?: string;
    priority_name?: string;
    sync_status: 'pending' | 'synced' | 'failed';
    sync_error?: string;
    sync_attempts: number;
    last_sync_attempt?: string;
    healthnet_synced: boolean;
    healthnet_sync_date?: string;
    healthnet_sync_error?: string;
    details_json?: any[];
    recup_raw_response?: any;
    created_at: string;
    updated_at: string;
}

export interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface PrescriptionSearchResponse {
    success: boolean;
    data: Prescription[];
    pagination: PaginationInfo;
    filters?: PrescriptionFilter;
}

export interface PrescriptionPreviewResponse {
    success: boolean;
    data: PrescriptionPreview[];
    filters?: PrescriptionFilter;
}

export interface SyncStatsResponse {
    success: boolean;
    data: {
        total: number;
        byStatus: {
            [key: string]: {
                count: number;
                avgAttempts: string;
                lastAttempt?: string;
                healthnetSynced: number;
            };
        };
        lastUpdate: string;
    };
}

// Interfaccia per le opzioni di cancellazione
export interface DeleteOptions {
    type: 'soft' | 'hard';
    reason?: string;
    isHardDelete: boolean;
}

// Risultato della cancellazione
export interface DeleteResult {
    success: boolean;
    message: string;
    details?: {
        local_deleted: boolean;
        recup_deleted: boolean;
        patient_fiscal_code?: string | null;
    };
    note?: string;
}

export interface BulkSyncRequest {
    prescriptionIds: string[];
    status: 'pending' | 'synced' | 'failed';
    error?: string;
}

export interface BulkHealthNetSyncRequest {
    prescriptionIds: string[];
    synced: boolean;
    error?: string;
}

class PrescriptionService {
    private getAuthToken(): string {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }
        return token;
    }

    private getHeaders() {
        return {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Search prescriptions with filters
     */
    async searchPrescriptions(filters: PrescriptionFilter = {}): Promise<PrescriptionSearchResponse> {
        try {
            const params = new URLSearchParams();
            
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });

            const response = await axios.get(
                `${API_BASE_URL}/api/prescriptions?${params.toString()}`,
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error searching prescriptions:', error);
            throw error;
        }
    }

    /**
     * Get prescription by ID
     */
    async getPrescriptionById(id: string, includeDetails: boolean = false): Promise<Prescription> {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/prescriptions/${id}?includeDetails=${includeDetails}`,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error getting prescription by ID:', error);
            throw error;
        }
    }

    /**
     * Get prescription by NRE
     */
    async getPrescriptionByNre(nre: string, includeDetails: boolean = false): Promise<Prescription> {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/prescriptions/nre/${nre}?includeDetails=${includeDetails}`,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error getting prescription by NRE:', error);
            throw error;
        }
    }

    /**
     * Get prescription preview for sync
     */
    async getPrescriptionPreview(filters: PrescriptionFilter = {}): Promise<PrescriptionPreviewResponse> {
        try {
            const params = new URLSearchParams();
            
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });

            const response = await axios.get(
                `${API_BASE_URL}/api/prescriptions/preview?${params.toString()}`,
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error getting prescription preview:', error);
            throw error;
        }
    }

    /**
     * Get sync statistics
     */
    async getSyncStats(): Promise<SyncStatsResponse> {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/prescriptions/sync/stats`,
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error getting sync stats:', error);
            throw error;
        }
    }

    /**
     * Create new prescription
     */
    async createPrescription(prescriptionData: Partial<Prescription>): Promise<Prescription> {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/prescriptions`,
                prescriptionData,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error creating prescription:', error);
            throw error;
        }
    }

    /**
     * Update prescription
     */
    async updatePrescription(id: string, updateData: Partial<Prescription>): Promise<Prescription> {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/prescriptions/${id}`,
                updateData,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error updating prescription:', error);
            throw error;
        }
    }

    /**
     * Delete prescription (soft or hard delete)
     */
    async deletePrescription(id: string, options: DeleteOptions): Promise<DeleteResult> {
        try {
            const params = new URLSearchParams();
            if (options.isHardDelete) {
                params.append('hard_delete', 'true');
            }

            const response = await axios.delete(
                `${API_BASE_URL}/api/prescriptions/${id}?${params.toString()}`,
                { 
                    headers: this.getHeaders(),
                    data: options.reason ? { reason: options.reason } : undefined
                }
            );
            
            return response.data;
        } catch (error) {
            console.error('Error deleting prescription:', error);
            throw error;
        }
    }

    /**
     * Backward compatibility: Delete prescription (legacy method for soft delete)
     */
    async deletePrescriptionLegacy(id: string): Promise<void> {
        await this.deletePrescription(id, {
            type: 'soft',
            isHardDelete: false
        });
    }

    /**
     * Update sync status
     */
    async updateSyncStatus(id: string, status: 'pending' | 'synced' | 'failed', error?: string): Promise<Prescription> {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/prescriptions/${id}/sync-status`,
                { status, error },
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error updating sync status:', error);
            throw error;
        }
    }

    /**
     * Update HealthNET sync status
     */
    async updateHealthNetSyncStatus(id: string, synced: boolean, error?: string): Promise<Prescription> {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/prescriptions/${id}/healthnet-sync-status`,
                { synced, error },
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error updating HealthNET sync status:', error);
            throw error;
        }
    }

    /**
     * Bulk update sync status
     */
    async bulkUpdateSyncStatus(request: BulkSyncRequest): Promise<any[]> {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/prescriptions/bulk/sync-status`,
                request,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error bulk updating sync status:', error);
            throw error;
        }
    }

    /**
     * Bulk update HealthNET sync status
     */
    async bulkUpdateHealthNetSyncStatus(request: BulkHealthNetSyncRequest): Promise<any[]> {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/prescriptions/bulk/healthnet-sync-status`,
                request,
                { headers: this.getHeaders() }
            );

            return response.data.data;
        } catch (error) {
            console.error('Error bulk updating HealthNET sync status:', error);
            throw error;
        }
    }

    /**
     * Sync single prescription by NRE (calls backend sync service)
     */
    async syncPrescriptionByNre(nre: string, options: any = {}): Promise<any> {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/sync/prescriptions/nre`,
                { nre, options },
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error syncing prescription by NRE:', error);
            throw error;
        }
    }

    /**
     * Sync prescriptions in batch (calls backend sync service)
     */
    async syncPrescriptionsBatch(nreList: string[], options: any = {}): Promise<any> {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/sync/prescriptions/batch`,
                { nreList, options },
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error syncing prescriptions batch:', error);
            throw error;
        }
    }

    /**
     * Sync prescriptions with HealthNET (special function)
     */
    async syncWithHealthNet(prescriptionIds: string[]): Promise<any> {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/sync/prescriptions/healthnet`,
                { prescriptionIds },
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('Error syncing prescriptions with HealthNET:', error);
            throw error;
        }
    }
}

export default new PrescriptionService();